import { useTranslation } from 'react-i18next';
import { FolderOpen, Receipt, Database, FileText } from 'lucide-react';
import { Reveal, RevealGroup, RevealItem } from './Reveal';

const USECASE_ICONS = [
  FolderOpen,
  Receipt,
  Database,
  FileText,
] as const;

const USECASE_KEYS = [
  'fileManagement',
  'receiptProcessing',
  'dataAnalysis',
  'meetingNotes',
] as const;

export default function UseCases() {
  const { t } = useTranslation();

  return (
    <section id="usecases" className="py-24 md:py-32 bg-[#faf9f5] dark:bg-[#141413]">
      <div className="mx-auto max-w-6xl px-6">

        {/* Section header */}
        <Reveal className="mb-16 text-center">
          <span className="eyebrow inline-flex items-center gap-2 rounded-full border border-[#1414131a] dark:border-[#faf9f51a] px-4 py-1.5 text-[#6b6a68] dark:text-[#9a9893]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#ae5630]" />
            {t('useCases.title')}
          </span>
          <h2 className="font-display display-tight mt-5 text-3xl font-medium leading-[1.1] text-[#141413] dark:text-[#faf9f5] md:text-5xl">
            {t('useCases.title')}
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-[#6b6a68] dark:text-[#9a9893]">
            {t('useCases.subtitle')}
          </p>
        </Reveal>

        {/* Asymmetric bento grid: first card spans 2 cols on md */}
        <RevealGroup className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
          {USECASE_KEYS.map((key, i) => {
            const Icon = USECASE_ICONS[i];
            const isHero = i === 0;
            return (
              <RevealItem
                key={key}
                className={`group flex flex-col rounded-2xl border border-[#1414131a] dark:border-[#faf9f51a] bg-[#fffefb] dark:bg-[#1a1918] shadow-warm transition-all duration-300 ease-spring hover:shadow-warm-lg hover:border-[#ae5630]/30 dark:hover:border-[#ae5630]/30 ${isHero ? 'md:col-span-2 lg:col-span-2' : ''}`}
              >
                <div className={`flex flex-col gap-4 p-7 ${isHero ? 'md:p-9' : ''} h-full`}>
                  {/* Icon */}
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#ae5630]/10 dark:bg-[#ae5630]/15 text-[#ae5630] transition-colors duration-300 ease-spring group-hover:bg-[#ae5630]/20 dark:group-hover:bg-[#ae5630]/25">
                    <Icon size={isHero ? 22 : 20} />
                  </div>

                  {/* Content */}
                  <div className="flex flex-1 flex-col">
                    <h3 className={`font-display font-medium text-[#141413] dark:text-[#faf9f5] leading-snug mb-2 ${isHero ? 'text-xl' : 'text-base'}`}>
                      {t(`useCases.${key}.title`)}
                    </h3>
                    <p className="flex-1 text-sm text-[#6b6a68] dark:text-[#9a9893] leading-relaxed mb-5">
                      {t(`useCases.${key}.desc`)}
                    </p>

                    {/* Time comparison badge */}
                    <div className="mt-auto flex items-center gap-2.5">
                      <span className="rounded-full border border-[#1414131a] dark:border-[#faf9f51a] px-3 py-1 text-xs text-[#87867f] line-through">
                        {t(`useCases.${key}.timeBefore`)}
                      </span>
                      <span className="text-[#ae5630]/60 text-xs">→</span>
                      <span className="rounded-full bg-[#ae5630]/10 dark:bg-[#ae5630]/15 px-3 py-1 text-xs font-medium text-[#ae5630]">
                        {t(`useCases.${key}.timeAfter`)}
                      </span>
                    </div>
                  </div>
                </div>
              </RevealItem>
            );
          })}
        </RevealGroup>
      </div>
    </section>
  );
}
